import React, { useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { suggestEmail } from '../utils/emailSuggest';

// ── Native Share Utility ────────────────────────────────────────────
// Tries Capacitor Share (iOS/Android native), then Web Share API, then clipboard.
// Returns: 'native' | 'web' | 'clipboard' | null

async function nativeShare({ title, text, url }) {
  // 1. Try Capacitor Share on native iOS/Android
  try {
    if (window.Capacitor?.isNativePlatform()) {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title, text, url });
      console.log('[ClariMe] Native share completed');
      return 'native';
    }
  } catch (err) {
    if (err?.message !== 'Share canceled') {
      console.log('[ClariMe] Capacitor Share failed, trying Web Share:', err?.message);
    } else {
      // User cancelled — not an error
      return null;
    }
  }

  // 2. Try Web Share API (Chrome on Android, Safari 12.1+)
  if (navigator.share) {
    try {
      await navigator.share({ title, url });
      console.log('[ClariMe] Web Share API completed');
      return 'web';
    } catch (err) {
      if (err?.name === 'AbortError') {
        // User cancelled
        return null;
      }
      console.log('[ClariMe] Web Share API failed, falling back to clipboard:', err?.message);
    }
  }

  // 3. Clipboard fallback
  try {
    await navigator.clipboard.writeText(url);
    console.log('[ClariMe] Share URL copied to clipboard');
    return 'clipboard';
  } catch (err) {
    console.log('[ClariMe] Clipboard write failed:', err?.message);
  }

  return null;
}

// ── Icons ───────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const MessageIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

// ── Manual Entry Form (web fallback) ────────────────────────────────

export const EmailIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

export const ManualEntryForm = ({ onAdd, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const keyboardHeight = useKeyboardHeight();

  const formatPhoneInput = (val) => {
    // Strip to digits only
    const digits = val.replace(/\D/g, '');
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
  const isValid = name.trim().length > 0 && isValidEmail(email);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    const digits = phone.replace(/\D/g, '');
    const hasPhone = digits.length >= 10;
    onAdd({
      id: `manual-${Date.now()}`,
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: hasPhone ? `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}` : '',
      phoneNormalized: hasPhone ? `+1${digits.slice(0, 10)}` : '',
      avatar: name.trim().charAt(0).toUpperCase(),
    });
    setName('');
    setEmail('');
    setPhone('');
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-[60] px-4 pt-[env(safe-area-inset-top)]"
      style={{ bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : 0 }}
    >
      <div className="bg-white rounded-2xl w-full max-w-sm flex flex-col shadow-2xl mt-16">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-lg text-slate-900">Add a recipient</h3>
            <button onClick={onClose} className="p-2 active:bg-slate-100 rounded-full text-slate-500">
              <CloseIcon />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sarah"
              className="w-full px-4 py-3 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 ring-teal-500/20"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="sarah@example.com"
              className="w-full px-4 py-3 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 ring-teal-500/20"
            />
            {suggestEmail(email) && (
              <button
                type="button"
                onClick={() => setEmail(suggestEmail(email))}
                className="mt-1.5 text-sm text-amber-600 font-medium active:text-amber-700"
              >
                Did you mean <span className="underline">{suggestEmail(email)}</span>?
              </button>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone number <span className="text-slate-400 font-normal">(optional)</span></label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 ring-teal-500/20"
            />
          </div>
          <button
            type="submit"
            disabled={!isValid}
            className={`w-full py-3 rounded-xl font-semibold transition-all ${
              isValid
                ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/20'
                : 'bg-slate-200 text-slate-400'
            }`}
          >
            Add recipient
          </button>
        </form>
      </div>
    </div>
  );
};

// ── Contact Picker Modal (native) ───────────────────────────────────

export const ContactPickerModal = ({ contacts, selectedContacts, onSelect, onClose, loading, permissionDenied }) => {
  const [search, setSearch] = useState('');
  const keyboardHeight = useKeyboardHeight();

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phone.includes(search) ||
    (c.email && c.email.toLowerCase().includes(search.toLowerCase()))
  );

  const isSelected = (contact) => selectedContacts.some(sc => sc.id === contact.id);

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end z-[60]"
      style={{ bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : 0 }}
    >
      <div className="bg-white rounded-t-3xl w-full max-h-[85vh] flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg text-slate-900">Choose Contacts</h3>
            <button onClick={onClose} className="p-2 active:bg-slate-100 rounded-full text-slate-500">
              <CloseIcon />
            </button>
          </div>

          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search contacts..."
              className="w-full pl-10 pr-4 py-3 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 ring-teal-500/20"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
              <span className="ml-3 text-slate-500 text-sm">Loading contacts...</span>
            </div>
          )}

          {permissionDenied && !loading && (
            <div className="text-center py-8 px-4">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              </div>
              <p className="text-slate-700 font-medium mb-1">Contact access denied</p>
              <p className="text-sm text-slate-500">Open Settings and allow ClariMe to access your contacts, then try again.</p>
            </div>
          )}

          {!loading && !permissionDenied && (
            <div className="space-y-2">
              {filteredContacts.map(contact => (
                <button
                  key={contact.id}
                  onClick={() => onSelect(contact)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                    isSelected(contact)
                      ? 'bg-teal-50 border-2 border-teal-300'
                      : 'bg-slate-50 border-2 border-transparent active:bg-slate-100'
                  }`}
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center text-xl font-medium text-slate-600">
                    {contact.avatar}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-slate-900">{contact.name}</p>
                    {contact.email && (
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <EmailIcon className="w-3.5 h-3.5" />
                        {contact.email}
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-sm text-slate-400 flex items-center gap-1">
                        <PhoneIcon />
                        {contact.phone}
                      </p>
                    )}
                  </div>
                  {isSelected(contact) && (
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white">
                      <CheckIcon />
                    </div>
                  )}
                </button>
              ))}
              {filteredContacts.length === 0 && !loading && (
                <p className="text-center text-slate-400 py-8">No contacts found</p>
              )}
            </div>
          )}
        </div>

        {selectedContacts.length > 0 && (
          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <button
              onClick={onClose}
              className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/20"
            >
              Done ({selectedContacts.length} selected)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Post-Share Success View ─────────────────────────────────────────
// Shown inside the modal AFTER email share is submitted.
// Offers native share sheet to send via iMessage, WhatsApp, etc.

const PostShareView = ({ shareUrl, emailsSent, onClose }) => {
  const [nativeShareResult, setNativeShareResult] = useState(null);

  // emailsSent === 0 means the backend created the link but found no one to email
  // (e.g. the group only contains the patient). undefined/null = unknown, treat as sent.
  const noEmails = emailsSent === 0;

  const handleNativeShare = async () => {
    if (!shareUrl) return;
    const result = await nativeShare({
      title: 'Visit Summary',
      text: 'I shared my doctor visit summary with you on ClariMe.',
      url: shareUrl,
    });
    if (result === 'clipboard') {
      setNativeShareResult('copied');
    }
  };

  return (
    <div className="p-6 flex flex-col items-center gap-5">
      {/* Success icon */}
      <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
        <svg className="w-8 h-8 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <div className="text-center">
        <h3 className="font-bold text-lg text-slate-900">{noEmails ? 'Share link ready' : 'Sent via email'}</h3>
        <p className="text-sm text-slate-500 mt-1">
          {noEmails
            ? 'No one in this group has an email address yet, so no email was sent. Send the link by text below.'
            : 'Recipients will receive the visit summary in their inbox.'}
        </p>
      </div>

      {/* Text-message share is the PRIMARY suggested action: a text from the
          patient's own phone gets seen; an email from notifications@ can sit
          unread or land in spam. */}
      {shareUrl && (
        <div className="w-full space-y-2">
          <button
            onClick={handleNativeShare}
            className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 shadow-lg shadow-teal-500/20 active:scale-[0.98] transition-all"
          >
            <MessageIcon className="w-5 h-5" />
            {nativeShareResult === 'copied' ? 'Link copied to clipboard!' : 'Send it by text too'}
          </button>
          <p className="text-xs text-slate-400 text-center">
            A text from you is the surest way your family sees it.
          </p>
        </div>
      )}

      <button
        onClick={onClose}
        className="w-full py-3 text-slate-500 text-sm font-medium active:text-slate-700"
      >
        Done
      </button>
    </div>
  );
};

// ── Share Modal ─────────────────────────────────────────────────────
// shareUrl — optional: the backend-generated share link for native share sheet.
//            onShare may also return { shareUrl, emailsSent } after the backend call.

// "Sarah", "Sarah and Michael", "Sarah, Michael and Linda", "Sarah, Michael and 2 more"
const describeGroupMembers = (group) => {
  const others = (group.members || []).filter(m => !m.isCurrentUser);
  if (others.length === 0) return null;
  const names = others.map(m => (m.name || m.email || 'Invited').trim().split(/\s+/)[0]);
  if (names.length === 1) return names[0];
  if (names.length <= 3) return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
};

const ShareModal = ({ groups, onShare, onClose, shareUrl }) => {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [shareResult, setShareResult] = useState(null);
  const [shareError, setShareError] = useState(null);

  const { contacts, loading, permissionDenied, hasContactAccess, method, loadContacts, pickOne } = useContacts();

  const toggleGroup = (groupId) => {
    setSelectedGroups(prev =>
      prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const toggleContact = (contact) => {
    setSelectedContacts(prev => {
      const exists = prev.some(c => c.id === contact.id);
      return exists ? prev.filter(c => c.id !== contact.id) : [...prev, contact];
    });
  };

  const addManualContact = (contact) => {
    setSelectedContacts(prev => [...prev, contact]);
    setShowManualEntry(false);
  };

  const removeContact = (contactId) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const handleShare = async () => {
    setSending(true);
    setShareError(null);
    try {
      // Notify parent (updates app state, triggers backend share call).
      // Parent may return { shareUrl, emailsSent } from the backend.
      const result = await onShare({ groupIds: selectedGroups, contacts: selectedContacts });
      setShareResult(result || null);
      // Transition to post-share success state (modal stays open)
      setSent(true);
    } catch (err) {
      console.error('[ShareModal] Share failed:', err?.message);
      setShareError('Could not share. Please check your connection and try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFromContacts = async () => {
    console.log('[ShareModal] From Contacts tapped, method:', method);

    // Native iOS: use the system pickContact picker — no permission prompt,
    // no bulk load, one contact per pick.
    if (method === 'native') {
      try {
        const picked = await pickOne();
        if (!picked) return; // cancelled / timed out
        setSelectedContacts(prev => {
          const key = picked.email || picked.phoneNormalized;
          if (prev.some(c => (c.email || c.phoneNormalized) === key)) return prev;
          return [...prev, picked];
        });
      } catch (err) {
        console.warn('[ShareModal] Native picker failed, falling back to manual:', err?.message);
        setShowManualEntry(true);
      }
      return;
    }

    if (method === 'web-picker') {
      try {
        const result = await loadContacts();
        if (result.length > 0) {
          setSelectedContacts(prev => {
            const existingIds = new Set(prev.map(c => c.email || c.phoneNormalized || c.id));
            const newOnes = result.filter(c => !existingIds.has(c.email || c.phoneNormalized));
            return [...prev, ...newOnes];
          });
        }
      } catch (err) {
        console.warn('[ShareModal] Web picker failed, showing manual entry:', err.message);
        setShowManualEntry(true);
      }
      return;
    }

    // Manual fallback
    setShowManualEntry(true);
  };

  const totalRecipients = selectedGroups.length + selectedContacts.length;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50">
        <div className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md overflow-hidden shadow-2xl max-h-[85vh] flex flex-col">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-lg text-slate-900">Share with Family</h3>
            <button onClick={onClose} className="p-2 -mr-2 rounded-full active:bg-slate-100 text-slate-400">
              <CloseIcon />
            </button>
          </div>

          {/* Post-share success state */}
          {sent ? (
            <PostShareView
              shareUrl={shareResult?.shareUrl || shareUrl}
              emailsSent={shareResult?.emailsSent}
              onClose={onClose}
            />
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {groups.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-slate-700 mb-3">Care Groups</p>
                    <div className="space-y-2">
                      {groups.map(group => (
                        <button
                          key={group.id}
                          onClick={() => toggleGroup(group.id)}
                          className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                            selectedGroups.includes(group.id)
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-slate-200 active:border-slate-300'
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg ${
                            selectedGroups.includes(group.id) ? 'bg-teal-500 text-white' : 'bg-slate-100'
                          }`}>
                            {group.emoji || '\u{1F468}\u200D\u{1F469}\u200D\u{1F467}'}
                          </div>
                          <div className="flex-1 text-left">
                            <p className={`font-medium ${selectedGroups.includes(group.id) ? 'text-teal-900' : 'text-slate-900'}`}>
                              {group.name}
                            </p>
                            {describeGroupMembers(group) ? (
                              <p className="text-sm text-slate-500">{describeGroupMembers(group)}</p>
                            ) : (
                              <p className="text-sm text-amber-600">Just you so far. No family will get this yet.</p>
                            )}
                          </div>
                          {selectedGroups.includes(group.id) && (
                            <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                              <CheckIcon className="text-white" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-sm text-slate-400">or send directly</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <div>
                  {selectedContacts.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {selectedContacts.map(contact => (
                        <div key={contact.id} className="flex items-center gap-3 p-3 bg-teal-50 border-2 border-teal-200 rounded-xl">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center text-lg font-medium text-slate-600">
                            {contact.avatar}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-teal-900">{contact.name}</p>
                            <p className="text-sm text-teal-600 flex items-center gap-1">
                              <EmailIcon className="w-3 h-3" />
                              {contact.email || contact.phone}
                            </p>
                          </div>
                          <button onClick={() => removeContact(contact.id)} className="p-1.5 rounded-full active:bg-teal-100 text-teal-500">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-3">
                    {hasContactAccess && (
                      <button
                        onClick={handleFromContacts}
                        className="flex-1 p-4 border-2 border-teal-300 bg-teal-50 rounded-xl text-teal-700 font-semibold active:bg-teal-100 transition-all flex flex-col items-center justify-center gap-1.5"
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        From Contacts
                      </button>
                    )}
                    <button
                      onClick={() => setShowManualEntry(true)}
                      className="flex-1 p-4 border-2 border-teal-300 bg-teal-50 rounded-xl text-teal-700 font-semibold active:bg-teal-100 transition-all flex flex-col items-center justify-center gap-1.5"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Enter Manually
                    </button>
                  </div>
                </div>

                {totalRecipients > 0 && (
                  <div className="bg-blue-50 rounded-xl p-4 flex gap-3">
                    <EmailIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      Recipients will receive the visit summary via email.
                    </p>
                  </div>
                )}
              </div>

              <div className="px-6 py-4 border-t border-slate-100 bg-slate-50">
                {shareError && (
                  <p className="text-sm text-red-600 text-center mb-3">{shareError}</p>
                )}
                <button
                  onClick={handleShare}
                  disabled={totalRecipients === 0 || sending}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                    totalRecipients > 0 && !sending
                      ? 'bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/25'
                      : 'bg-slate-200 text-slate-400'
                  }`}
                >
                  <MessageIcon className="w-5 h-5" />
                  {sending
                    ? 'Sending...'
                    : totalRecipients > 0
                      ? `Send to ${totalRecipients} ${totalRecipients === 1 ? 'recipient' : 'recipients'}`
                      : 'Select recipients'
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showContactPicker && (
        <ContactPickerModal
          contacts={contacts}
          selectedContacts={selectedContacts}
          onSelect={toggleContact}
          onClose={() => setShowContactPicker(false)}
          loading={loading}
          permissionDenied={permissionDenied}
        />
      )}

      {showManualEntry && (
        <ManualEntryForm
          onAdd={addManualContact}
          onClose={() => setShowManualEntry(false)}
        />
      )}
    </>
  );
};

export default ShareModal;
